import { RefCallback, useCallback, useEffect, useRef, useState } from 'react';
import isFunction from 'lodash/isFunction';

type OptionsType = {
  controlMouseLeave?: (event: MouseEvent) => boolean;
};

export type UseHoverOptionsType = OptionsType;

export function useHover<T extends Element>(
  condition = true,
  { controlMouseLeave }: OptionsType = {}
): [RefCallback<T>, boolean] {
  const [hovering, setHovering] = useState(false);
  const previousNode = useRef<T | null>(null);

  const handleMouseEnter = useCallback(() => {
    setHovering(true);
  }, []);

  const handleMouseLeave = useCallback(
    (event: Event) => {
      if (isFunction(controlMouseLeave)) {
        setHovering(controlMouseLeave(event as MouseEvent));
      } else {
        setHovering(false);
      }
    },
    [controlMouseLeave]
  );

  const customRef = useCallback<RefCallback<T>>(
    (node) => {
      if (previousNode.current) {
        previousNode.current.removeEventListener('mouseenter', handleMouseEnter);
        previousNode.current.removeEventListener('mouseleave', handleMouseLeave);
      }

      if (node && condition) {
        node.addEventListener('mouseenter', handleMouseEnter);
        node.addEventListener('mouseleave', handleMouseLeave);
      }

      previousNode.current = node;
    },
    [handleMouseEnter, handleMouseLeave, condition]
  );

  useEffect(() => {
    if (!condition && previousNode.current) {
      previousNode.current.removeEventListener('mouseenter', handleMouseEnter);
      previousNode.current.removeEventListener('mouseleave', handleMouseLeave);
    }
  }, [condition, handleMouseEnter, handleMouseLeave]);

  return [customRef, hovering];
}
